import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
	Box,
	Button,
	CircularProgress,
	Typography,
	Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import useSalesforceReportAnalysis from 'hooks/useSalesforceReportAnalysis';
import SuggestedRulesList from '../components/AlertCenter/SuggestedRulesList';
import CustomizeReportAlertForm from '../components/AlertCenter/CustomizeReportAlertForm';

const CenteredContent = ({ children }) => {
	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 400,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				textAlign: 'center',
				px: 3,
				pb: 32, // slightly offsets the content above true center
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					maxWidth: 460,
				}}
			>
				{children}
			</Box>
		</Box>
	);
};

const LoadingState = ({ reportName }) => {
	return (
		<CenteredContent>
			<CircularProgress size={44} thickness={4} sx={{ mb: 3 }} />

						<Typography
							variant='h1'
							sx={{
								fontWeight: 700,
								mb: 2,
							}}
						>
							Analyzing report...
						</Typography>

						<Typography
							variant='body2'
							color='text.secondary'
							sx={{
								lineHeight: 1.6,
							}}
						>
							Reading fields, groupings, and thresholds in{' '}
							<Typography
								component='span'
								variant='inherit'
								sx={{
									fontWeight: 700,
									color: 'text.primary',
								}}
							>
								{reportName}
							</Typography>{' '}
							to generate suggested alerts.
						</Typography>

						<Typography
							variant='body2'
							color='text.secondary'
							sx={{
								lineHeight: 1.6,
								mt: 2,
							}}
						>
							This may take a few minutes.
						</Typography>
		</CenteredContent>
	);
};

const ErrorState = ({ reportName }) => {
	return (
		<CenteredContent>
			<ErrorOutlineIcon fontSize='large' color='error' />

			<Typography
				variant='h1'
				sx={{
					fontWeight: 700,
					mb: 2,
				}}
			>
				Unable to generate suggested alerts
			</Typography>

			<Typography
				color='text.secondary'
				sx={{
					lineHeight: 1.6,
				}}
			>
				We were unable to generate suggested alerts for{' '}
				<Typography
					component='span'
					variant='inherit'
					fontWeight={700}
					color='text.primary'
				>
					{reportName}.
				</Typography>{' '}
				Please try again later.
			</Typography>
		</CenteredContent>
	);
};

const GenerateAlertFromReport = () => {
	const navigate = useNavigate();
	const { reportId } = useParams();
	const { state } = useLocation();
	const [selectedRule, setSelectedRule] = useState(null);
	const reportName = state?.reportName || 'this report';

	const { suggestedRules, loading, error } = useSalesforceReportAnalysis(reportId);

	useEffect(() => {
		if (suggestedRules?.length > 0) {
			setSelectedRule(suggestedRules[0]);
		}
	}, [suggestedRules]);

	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				overflowY: 'auto',
				backgroundColor: 'background.default',
				padding: '12px 20px',
				display: 'flex',
				flexDirection: 'column',
				gap: '14px',
			}}
		>
			<Button
				disabled={loading} // disabled during analysis until we know how we want to handle 'canceling' request
				startIcon={<ArrowBackIcon />}
				onClick={() => navigate('../new')}
				sx={{ alignSelf: 'flex-start' }}
			>
				Reports
			</Button>

			{/* Loading state */}
			{loading && <LoadingState reportName={reportName} />}

			{/* Error state */}
			{!loading && error && <ErrorState reportName={reportName} />}

			{/* Main content displayed when not loading and no error */}
			{!loading && !error && (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					<Typography variant={'h1'}>New alert - {reportName}</Typography>
					{suggestedRules?.length === 0 ? (
						<Typography color='text.secondary'>
							No suggested alerts were generated.
						</Typography>
					) : (
						<Stack spacing={2}>
							<Typography color='text.secondary' variant='overline'>
								Suggested - Pick one, then adjust
							</Typography>

							{/* List of suggested rules */}
							<SuggestedRulesList
								suggestedRules={suggestedRules}
								setSelectedRule={setSelectedRule}
								selectedRule={selectedRule}
							/>

							{/* Form to customize and create alert */}
							{selectedRule && (
								<CustomizeReportAlertForm
									selectedAlert={selectedRule}
									reportId={reportId}
								/>
							)}
						</Stack>
					)}
				</Box>
			)}
		</Box>
	);
};

export default GenerateAlertFromReport;
